import axios from 'axios';

declare global {
  interface Window {
    DOTVOTING_API_BASE_URL?: string;
  }
}

const resolveApiBaseUrl = () => {
  if (typeof window !== 'undefined') {
    const runtimeApiBaseUrl = window.DOTVOTING_API_BASE_URL?.trim();
    if (runtimeApiBaseUrl) {
      return runtimeApiBaseUrl;
    }
  }

  return import.meta.env.VITE_API_BASE_URL ?? 'http://localhost:8082';
};

export const API_BASE_URL = resolveApiBaseUrl();
const DOTSTORMING_ACCESS_TOKEN_KEY = 'dotstorming_access_token';
const DOTSTORMING_REFRESH_TOKEN_KEY = 'dotstorming_refresh_token';
const TOKEN_REFRESH_WINDOW_MS = 10 * 60 * 1000;

export class ApiError extends Error {
  readonly status: number;

  constructor(status: number, message: string) {
    super(message);
    this.name = 'ApiError';
    this.status = status;
  }
}

type RefreshTokenResponse = {
  token?: {
    accessToken?: string;
    refreshToken?: string;
  };
};

const normalizeErrorMessage = (status: number, payload: unknown) => {
  if (typeof payload === 'string' && payload.trim()) {
    return payload;
  }

  if (payload && typeof payload === 'object' && 'message' in payload) {
    const message = (payload as { message?: unknown }).message;
    if (typeof message === 'string' && message.trim()) {
      return message;
    }
  }

  return `Request failed with status ${status}`;
};

const decodeBase64Url = (value: string) => {
  if (typeof atob !== 'function') {
    return null;
  }

  const padded = value.padEnd(Math.ceil(value.length / 4) * 4, '=');
  const normalized = padded.replace(/-/g, '+').replace(/_/g, '/');
  return atob(normalized);
};

const parseTokenExpiry = (token: string): Date | null => {
  const tokenParts = token.split('.');
  if (tokenParts.length < 2) {
    return null;
  }

  const payloadText = decodeBase64Url(tokenParts[1]);
  if (!payloadText) {
    return null;
  }

  try {
    const payload = JSON.parse(payloadText) as { exp?: number };
    if (typeof payload.exp !== 'number' || !Number.isFinite(payload.exp)) {
      return null;
    }

    return new Date(payload.exp * 1000);
  } catch {
    return null;
  }
};

const readLocalStorageValue = (key: string) => {
  if (typeof window === 'undefined') {
    return null;
  }

  const value = window.localStorage.getItem(key);
  const trimmed = value?.trim() ?? '';
  return trimmed.length > 0 ? trimmed : null;
};

const writeLocalStorageValue = (key: string, value: string | null) => {
  if (typeof window === 'undefined') {
    return;
  }

  if (!value) {
    window.localStorage.removeItem(key);
    return;
  }

  window.localStorage.setItem(key, value);
};

const getBearerTokenFromHeaders = (headers?: HeadersInit) => {
  const authorization = headers ? new Headers(headers).get('Authorization') : null;
  if (!authorization?.startsWith('Bearer ')) {
    return null;
  }

  const token = authorization.slice('Bearer '.length).trim();
  return token || null;
};

const isTokenWithinRefreshWindow = (token: string) => {
  const expiry = parseTokenExpiry(token);
  if (!expiry) {
    return false;
  }

  return expiry.getTime() - Date.now() <= TOKEN_REFRESH_WINDOW_MS;
};

export const refreshLocalStorageAccessTokenIfNeeded = async (headers: Headers) => {
  const bearerToken = getBearerTokenFromHeaders(headers);
  if (!bearerToken) {
    return;
  }

  const storedAccessToken = readLocalStorageValue(DOTSTORMING_ACCESS_TOKEN_KEY);
  if (!storedAccessToken || storedAccessToken !== bearerToken) {
    return;
  }

  if (!isTokenWithinRefreshWindow(storedAccessToken)) {
    return;
  }

  const refreshToken = readLocalStorageValue(DOTSTORMING_REFRESH_TOKEN_KEY);
  if (!refreshToken) {
    return;
  }

  try {
    const response = await axios.post<RefreshTokenResponse>(
      `${API_BASE_URL}/users/refreshToken`,
      { token: refreshToken },
      {
        headers: {
          'Content-Type': 'application/json',
        },
      }
    );

    const payload = response.data;
    const refreshedAccessToken = payload?.token?.accessToken?.trim() ?? null;
    const refreshedRefreshToken = payload?.token?.refreshToken?.trim() ?? null;

    writeLocalStorageValue(DOTSTORMING_ACCESS_TOKEN_KEY, refreshedAccessToken);
    writeLocalStorageValue(DOTSTORMING_REFRESH_TOKEN_KEY, refreshedRefreshToken ?? refreshToken);

    if (refreshedAccessToken) {
      headers.set('Authorization', `Bearer ${refreshedAccessToken}`);
    }
  } catch {
    // Ignore refresh failures and continue the request with existing token.
  }
};

export const apiRequest = async <T>(
  path: string,
  init?: RequestInit,
  headers?: Record<string, string>
): Promise<T> => {
  const requestHeaders = new Headers(init?.headers ?? undefined);
  if (!requestHeaders.has('Content-Type')) {
    requestHeaders.set('Content-Type', 'application/json');
  }

  Object.entries(headers ?? {}).forEach(([key, value]) => {
    requestHeaders.set(key, value);
  });

  await refreshLocalStorageAccessTokenIfNeeded(requestHeaders);

  const axiosHeaders: Record<string, string> = {};
  requestHeaders.forEach((value, key) => {
    axiosHeaders[key] = value;
  });

  try {
    const response = await axios.request<T>({
      url: `${API_BASE_URL}${path}`,
      method: init?.method,
      data: init?.body,
      headers: axiosHeaders,
      signal: init?.signal ?? undefined,
    });

    if (response.status === 204 || response.data === '' || response.data == null) {
      return undefined as T;
    }

    return response.data as unknown as T;
  } catch (error) {
    if (axios.isAxiosError(error) && error.response) {
      const status = error.response.status;
      const message = normalizeErrorMessage(status, error.response.data);
      throw new ApiError(status, message);
    }

    throw error;
  }
};
